import * as React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/ui/notify";
import { Button } from "@/components/ui/button";
import { TableSkeleton } from "@/components/ui/table";
import { Notify } from "@/components/ui/notify";
import {
  FilterChipStrip,
  type FilterChipStripValue,
} from "@/features/lines/list/components/FilterChipStrip";
import { useLines, useDeleteLine, type LineSummary } from "@/features/lines/hooks";
import { useOeeThresholds } from "@/hooks/useOrganizationPolicy";
import { useAuthStore } from "@/stores/auth.store";
import { useRealtimeRooms } from "@/lib/use-realtime-rooms";
import { ApiClientError, getFriendlyErrorMessage } from "@/lib/api-client";
import { LinesTable } from "./LinesTable";
import { LinesEmptyState } from "./LinesEmptyState";

export function LinesPage(): React.ReactElement {
  const me = useAuthStore((s) => s.user)!;
  const navigate = useNavigate();
  const linesQuery = useLines();
  const { data: lines = [], isLoading } = linesQuery;
  const deleteLine = useDeleteLine();
  // Entra nos rooms das linhas listadas (room-per-line) p/ eventos ao vivo.
  useRealtimeRooms(React.useMemo(() => lines.map((l) => l.id), [lines]));

  const [filters, setFilters] = React.useState<FilterChipStripValue>({
    status: "all",
    responsible: "all",
    oee: "all",
  });

  const responsibles = React.useMemo(
    () =>
      Array.from(
        new Set(lines.map((l) => l.responsible).filter((r): r is string => !!r && r.length > 0))
      ).sort(),
    [lines]
  );

  // Spec 079 — os baldes "alto/médio/baixo" usam a MESMA régua que pinta a barra ao lado. Eram
  // `85`/`65` escritos aqui: filtrar por "OEE alto" devolvia linhas que a coluna pintava de amarelo.
  const thresholds = useOeeThresholds();

  const filtered = React.useMemo(() => {
    return lines.filter((l) => {
      if (filters.status !== "all" && l.status !== filters.status) return false;
      if (filters.responsible !== "all" && l.responsible !== filters.responsible) return false;
      if (filters.oee !== "all") {
        const a = l.availabilityEstimate7d;
        if (a === null) return false;
        const pct = a * 100;
        if (filters.oee === "high" && pct < thresholds.good) return false;
        if (filters.oee === "mid" && (pct < thresholds.warn || pct >= thresholds.good))
          return false;
        if (filters.oee === "low" && pct >= thresholds.warn) return false;
      }
      return true;
    });
  }, [lines, filters, thresholds]);

  const totalMachines = filtered.reduce((s, l) => s + l.machineCount, 0);
  const isGestor = me.role === "GESTOR";

  async function askDelete(line: LineSummary) {
    const ok = await Notify.confirm({
      variant: "danger",
      title: `Excluir linha "${line.name}"?`,
      description:
        "Esta ação não pode ser desfeita. O histórico será preservado se a linha tiver dados associados.",
      typeToConfirm: line.name,
      confirmLabel: "Excluir linha",
    });
    if (ok) await performDelete(line);
  }

  async function performDelete(line: LineSummary, force?: boolean) {
    try {
      await deleteLine.mutateAsync({ id: line.id, force });
      toast.success(`Linha "${line.name}" excluída.`);
    } catch (err) {
      if (!force && err instanceof ApiClientError && err.error.code === "HAS_HISTORY") {
        toast.error(err.error.message, {
          description: "Use o botão Forçar para excluir preservando o histórico.",
          action: {
            label: "Forçar",
            onClick: () => void performDelete(line, true),
          },
        });
        return;
      }
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao excluir.", { description: msg });
    }
  }

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[28px] font-semibold tracking-tight text-text-primary">
            Linhas de Produção
          </h1>
          <p className="mt-1 text-[13px] text-text-secondary">
            Modele e monitore o chão de fábrica.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isGestor ? (
            <Button size="sm" onClick={() => navigate("/lines/new")}>
              + Linha
            </Button>
          ) : null}
        </div>
      </header>

      <FilterChipStrip
        value={filters}
        onChange={setFilters}
        responsibles={responsibles}
        counters={{ lines: filtered.length, machines: totalMachines }}
      />

      {linesQuery.isError ? (
        <Notify.QueryError error={linesQuery.error} onRetry={() => void linesQuery.refetch()} />
      ) : isLoading ? (
        <TableSkeleton columns={8} rows={6} />
      ) : filtered.length === 0 ? (
        <LinesEmptyState
          totalLines={lines.length}
          isGestor={isGestor}
          onCreate={() => navigate("/lines/new")}
        />
      ) : (
        <LinesTable
          lines={filtered}
          isGestor={isGestor}
          onOpenDetail={(id) => navigate(`/lines/${id}/detail`)}
          onEdit={(id) => navigate(`/lines/${id}/edit`)}
          onAskDelete={(l) => void askDelete(l)}
        />
      )}
    </section>
  );
}
